import API from 'shared/lib/api'
import cloneDeep from 'lodash/cloneDeep'

// Module-scoped cache for optimistic delete operations
const optimisticDeleteCache = new Map()

// Helper function to clean up cache entries and prevent memory leaks
function cleanupCacheEntry (model, id) {
  const modelCache = optimisticDeleteCache.get(model.pluralName)
  if (modelCache) {
    modelCache.delete(id)
    // Clean up empty model cache to prevent memory leaks
    if (modelCache.size === 0) {
      optimisticDeleteCache.delete(model.pluralName)
    }
  }
}

// Export cache for testing purposes
export { optimisticDeleteCache }

export default {

  // Delete resource from the API.
  //
  // @example
  //   SomeModel.actions.delete(id: 1, optimistic: true)
  //
  actionMethod: (model, options = {}) => {
    options.apiVersion = options.apiVersion ?? model.options.apiVersion

    return API.deleteResource(model.pluralName, options.id, options)
  },

  // Updates record (optimistically) in the collection.
  //
  reduceMethod: (model, collection, action) => {
    const options = action?.payload?.options || {}
    const id = options.id

    switch (action?.type) {
      case model.actionTypes.DELETE_START:
        if (id) {
          // Set delete flags first
          collection = collection.update(id, { isBusy: true, pendingDelete: true })

          // Store original record data before optimistic deletion
          if (options.optimistic) {
            const originalRecord = collection.getById(id)
            if (originalRecord) {
              // Store the original record data in module cache for restoration
              // Use nested structure to avoid key collisions: cache[model][id]
              if (!optimisticDeleteCache.has(model.pluralName)) {
                optimisticDeleteCache.set(model.pluralName, new Map())
              }
              const modelCache = optimisticDeleteCache.get(model.pluralName)
              // Use deep clone to prevent shared references with nested objects/arrays
              const recordData = originalRecord.toJS ? originalRecord.toJS() : cloneDeep(originalRecord)
              modelCache.set(id, recordData)

              // Optimistic delete record from collection
              collection = collection.delete(id)
            }
          }
        }

        return collection

      case model.actionTypes.DELETE_ERROR:
        if (id) {
          if (options.optimistic) {
            // Try to restore the deleted record from cache
            const modelCache = optimisticDeleteCache.get(model.pluralName)
            const cachedRecord = modelCache?.get(id)

            if (cachedRecord) {
              // Restore the deleted record with error state
              // Set pendingDelete: false since the operation completed (with error)
              collection = collection.add(id, {
                ...cachedRecord,
                isBusy: false,
                pendingDelete: false,
                hasError: true,
                errorData: action?.payload?.error
              })

              // Clean up the cache
              cleanupCacheEntry(model, id)
            } else {
              // Cache is missing - clean up any stale cache entries
              cleanupCacheEntry(model, id)

              // Create a minimal record with error state
              // This ensures users see an error indication even when cache fails
              // Set pendingDelete: false since the operation completed (with error)
              collection = collection.add(id, {
                id,
                isBusy: false,
                pendingDelete: false,
                hasError: true,
                errorData: action?.payload?.error
              })
            }
          } else {
            // Non-optimistic delete error - just set error flags
            // Set pendingDelete: false since the operation completed (with error)
            collection = collection.update(id, {
              isBusy: false,
              pendingDelete: false,
              hasError: true,
              errorData: action?.payload?.error
            })
          }
        }

        return collection

      case model.actionTypes.DELETE_SUCCESS:
        // Clean up optimistic delete cache if it exists
        if (id) {
          cleanupCacheEntry(model, id)
        }

        // Because of pusher, it is possible to have two DELETE_SUCCESS events, but only one DELETE_START
        // To ensure idempotency we must double check that is present in the collection, before attempting
        // to update its `pendingDelete` state
        if (id && collection.getById(id)) {
          // Remove delete flags
          collection = collection.update(id, { isBusy: false, pendingDelete: false })
        }

        // Delete record
        return collection.delete(id)
    }
  }
}
